import {
	genericMemo,
	LuIcon,
	Menu,
	type MenuClickTriggerProps,
	type MenuRootProps,
	type MenuSelectGroupProps,
	type MenuVariantProps,
	Text,
} from "@overmap-ai/blocks"
import { type Stored, submittedAtModelSortFn } from "@overmap-ai/common"
import { useSort } from "@overmap-ai/hooks"
import { cx } from "class-variance-authority"
import { type ReactElement } from "react"

import { modelIconMapping } from "../../mappings/icons"
import type { FieldsRevisionModel } from "../FieldsRevisionsTimeline/typings"
import { SelectItemTemplate } from "../MenuGroups/constants"

type FieldsRevisionDropdownSelectProps<TModel extends FieldsRevisionModel> = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	MenuVariantProps &
	MenuSelectGroupProps<TModel> & {
		children: ReactElement
		current?: TModel
		fieldsRevisions: TModel[]
	}

export const FieldsRevisionDropdownSelect = genericMemo(function <TModel extends FieldsRevisionModel>(
	props: FieldsRevisionDropdownSelectProps<TModel>,
) {
	const {
		disabled = false,
		children,
		radius,
		size,
		variant,
		accentColor,
		fieldsRevisions,
		required,
		defaultValue,
		value,
		current,
		onValueChange,
		...rest
	} = props

	const sortedFieldsRevisions = useSort(fieldsRevisions, submittedAtModelSortFn)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.Scroll>
					<Menu.SelectGroup<Stored<TModel>>
						required={required}
						defaultValue={defaultValue}
						value={value}
						// @ts-expect-error REASON: typescript can figure out discriminator
						onValueChange={onValueChange}
					>
						{sortedFieldsRevisions.map((fieldsRevision) => {
							return (
								<SelectItemTemplate<Stored<TModel>> key={fieldsRevision.uuid} value={fieldsRevision}>
									{current ? (
										<LuIcon
											className={cx("align-center inline", {
												"opacity-0": current?.uuid !== fieldsRevision?.uuid,
											})}
											icon={modelIconMapping.stage}
											size={6}
											color="var(--primary-indicator)"
											fill="var(--primary-indicator)"
										/>
									) : null}

									<Text className="truncate">Revision {fieldsRevision.revision}</Text>
								</SelectItemTemplate>
							)
						})}
					</Menu.SelectGroup>
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
